import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { CheckCircle, XCircle } from "lucide-react";

import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./layout/DashboardLayout/DashboardLayout";
import CreateTournamentLayout from "./layout/TournamentLayout/TournamentLayout";
import { TournamentCreationProvider } from "./context/TournamentCreationContext";
import ForgotPasswordWithEmail from "./pages/Authentication/ForgotPasswordWithEmail";
import ForgotPasswordWithMobile from "./pages/Authentication/ForgotPasswordWithMobile";
import LoginOptions from "./pages/Authentication/LoginOptions";
import LoginWithEmail from "./pages/Authentication/LoginWithEmail";
import LoginWithMobile from "./pages/Authentication/LoginWithMobile";
import ResetPasswordWithEmail from "./pages/Authentication/ResetPasswordWithEmail";
import ResetPasswordWithMobile from "./pages/Authentication/ResetPasswordWithMobile";
import UpdatePassword from "./pages/Authentication/UpdatePassword";
import ChooseDurationType from "./pages/CreateTournament/ChooseDurationType";
import ChooseSport from "./pages/CreateTournament/ChooseSport";
import ChooseTournament from "./pages/CreateTournament/ChooseTournament";
import ChooseVenueType from "./pages/CreateTournament/ChooseVenueType";
import CreateTournamentDivision from "./pages/CreateTournament/CreateTournamentDivision";
import CreateTournamentGroups from "./pages/CreateTournament/CreateTournamentGroups";
import SetMatchRules from "./pages/CreateTournament/SetMatchRules";
import SetMatchSchedule from "./pages/CreateTournament/SetMatchSchedule";
import TeamCount from "./pages/CreateTournament/TeamCount";
import TeamRegistrationFee from "./pages/CreateTournament/TeamRegistrationFee";
import TournamentLogo from "./pages/CreateTournament/TournamentLogo";
import TournamentName from "./pages/CreateTournament/TournamentName";
import Fixtures from "./pages/fixtures";
import HomeV2 from "./pages/dashboard/HomeV2";
import Members from "./pages/Participants/index";
import Organisation from "./pages/Organisation/Organisation";
import OrganisationMembersV2 from "./pages/Organisation/OrganisationMembersV2";
import OrganisationsV2 from "./pages/Organisation/OrganisationsV2";
import { 
  PaymentsList,
  PaymentDetails,
  PaymentCardDetails,
  ParticipantPayment,
  FundraisingList,
  CreateFundraising,
  FundraisingDetails
} from "./pages/payments";
import Results from "./pages/tournament/results/Results";
import SettingsV2 from "./pages/tournament/settings/SettingsV2";
import StandingsV2 from "./pages/tournament/standings/StandingsV2";
import Format from "./pages/tournament/format/FormatRefactored";
import StatisticsV2 from "./pages/tournament/statistics/StatisticsV2";
import TeamOverview from "./pages/TeamOverview/index";
import Competitions from "./pages/competitions/CompetitionsRefactored";
import CompetitionsConfig from "./pages/competitions/CompetitionConfigurationRefactored";
import CompetitionsVenue from "./pages/competitions/CompetitionVenueRefactored";
import CompetitionsFee from "./pages/competitions/CompetitionFeesRefactored";
import Notifications from "./pages/dashboard/Notifications";
import { AUTH_STACK, DASHBOARD_STACK } from "./utils/screenNames";
import ChooseResultManagement from "./pages/CreateTournament/ChooseResultManagement";
import PublicDashboardLayout from "./layout/PublicDashboardLayout/PublicDashboardLayout";
import InvitationResponse from "./pages/dashboard/InvitationResponse";
import MemberInvitationResponse from "./pages/dashboard/MemberInvitationResponse";
import TeamDashboard from "./pages/dashboard/TeamDashboard";
import SignUp from "./pages/Authentication/SignupOptions";
import SignupVerification from "./pages/Authentication/SignupVerification";
import PersonalDetails from "./pages/Authentication/PersonalDetails";
import LocationDetails from "./pages/Authentication/LocationDetails";
import OrganisationDetails from "./pages/Authentication/OrganisationDetails";
import ManualGrid from "./components/Format/ManualGrid";
import StripeConnectReturn from "./pages/payments/StripeConnectReturn";
import StripeConnectRefresh from "./pages/payments/StripeConnectRefresh";
import { identifyUser, trackPage } from "./utils/analytics";

import "./utils/styles.css"

function AnalyticsBootstrap() {
  const user = useSelector(state => state.user?.user)
  useEffect(() => {
    if (user?.id) identifyUser(user)
  }, [user?.id])
  return null
}

function RouteAnalytics() {
  const location = useLocation()
  useEffect(() => {
    const clean = location.pathname.replace(/[0-9a-f]{24}/gi, ':id')
    trackPage(clean, { path: clean })
  }, [location.pathname])
  return null
}

function App() {
  return (
    <>
      <AnalyticsBootstrap />
      <RouteAnalytics />
      <Routes>
        {/* Auth Routes */}
        <Route path={AUTH_STACK.LOGIN_OPTIONS} element={<LoginOptions />} />

        <Route
          path={AUTH_STACK.LOGIN_WITH_EMAIL}
          element={<LoginWithEmail />}
        />
        <Route
          path={AUTH_STACK.SIGNUP_OPTIONS}
          element={<SignUp />}
        />
        <Route
          path={AUTH_STACK.SIGNUP_VERIFY}
          element={<SignupVerification />}
        />
        <Route
          path={AUTH_STACK.PERSONAL_DETAILS}
          element={<PersonalDetails />}
        />
        <Route
          path={AUTH_STACK.LOCATION_DETAILS}
          element={<LocationDetails />}
        />
        <Route
          path={AUTH_STACK.ORGANISATION_DETAILS}
          element={<OrganisationDetails />}
        />
        <Route
          path={AUTH_STACK.LOGIN_WITH_MOBILE}
          element={<LoginWithMobile />}
        /> 

        <Route
          path={AUTH_STACK.FORGOT_PASSWORD_WITH_EMAIL}
          element={<ForgotPasswordWithEmail />}
        />
        <Route
          path={AUTH_STACK.FORGOT_PASSWORD_WITH_MOBILE}
          element={<ForgotPasswordWithMobile />}
        />

        <Route
          path={AUTH_STACK.RESET_PASSWORD_WITH_EMAIL}
          element={<ResetPasswordWithEmail />}
        />
        <Route
          path={AUTH_STACK.RESET_PASSWORD_WITH_MOBILE}
          element={<ResetPasswordWithMobile />}
        />
        <Route
          path={AUTH_STACK.UPDATE_PASSWORD}
          element={<UpdatePassword />}
        />

        {/* Public View Route */}
        <Route path="/public/:tournamentId" element={<PublicDashboardLayout />}>
          <Route index element={<Navigate to="fixtures" replace />} />
          <Route path="fixtures" element={<Fixtures />} />
          <Route path="standings" element={<StandingsV2 />} />
          <Route path="statistics" element={<StatisticsV2 />} />
        </Route>

        {/* Public Invitation Response Route (no auth required) */}
        <Route path="/invitation/:token" element={<InvitationResponse />} />

        {/* Public Member Invitation Route (referee/admin - auth required to accept) */}
        <Route path="/member-invitation/:token" element={<MemberInvitationResponse />} />

        {/* Team Dashboard Route (after invitation acceptance) */}
        <Route path="/team/:teamId/dashboard" element={<TeamDashboard />} />

        {/* Dashboard Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeV2 />} />
          <Route path={DASHBOARD_STACK.MEMBERS} element={<Members />} />
          <Route path={DASHBOARD_STACK.FIXTURES} element={<Fixtures />} />
          <Route path={DASHBOARD_STACK.PAYMENTS} element={<PaymentsList />} />
          <Route
            path={DASHBOARD_STACK.PAYMENT_DETAILS}
            element={<PaymentDetails />}
          />
          <Route
            path={DASHBOARD_STACK.STRIPE_CONNECT_RETURN}
            element={<StripeConnectReturn />}
          />
          <Route
            path={DASHBOARD_STACK.STRIPE_CONNECT_REFRESH}
            element={<StripeConnectRefresh />}
          />
          <Route path={DASHBOARD_STACK.RESULTS} element={<Results />} />
          <Route path={DASHBOARD_STACK.STATISTICS} element={<StatisticsV2 />} />
          <Route path={DASHBOARD_STACK.STANDINGS} element={<StandingsV2 />} />
          <Route path={DASHBOARD_STACK.FORMAT} element={<Format />} />
          <Route path={DASHBOARD_STACK.MANUALGRID} element={<ManualGrid />} />
          <Route path={DASHBOARD_STACK.NOTIFICATIONS} element={<Notifications />} />
          <Route
            path={DASHBOARD_STACK.ORGANISATION_MEMBERS}
            element={<OrganisationMembersV2 />}
          />
          <Route
            path={DASHBOARD_STACK.ORGANISATIONS}
            element={<OrganisationsV2 />}
          />
          <Route path={DASHBOARD_STACK.SETTINGS} element={<SettingsV2 />} />
          <Route
            path={DASHBOARD_STACK.TEAM_OVERVIEW}
            element={<TeamOverview />}
          />
          <Route element={<TournamentCreationProvider><Outlet /></TournamentCreationProvider>}>
            <Route path={DASHBOARD_STACK.COMPETITIONS} element={<Competitions />} />
            <Route path={DASHBOARD_STACK.COMPETITIONS_CONFIG} element={<CompetitionsConfig />} />
            <Route path={DASHBOARD_STACK.COMPETITIONS_VENUE} element={<CompetitionsVenue />} />
            <Route path={DASHBOARD_STACK.COMPETITIONS_FEE} element={<CompetitionsFee />} />
          </Route>
        </Route>

        {/* Create Competition Routes */}
        <Route
          element={
            <ProtectedRoute>
              <TournamentCreationProvider>
                <CreateTournamentLayout />
              </TournamentCreationProvider>
            </ProtectedRoute>
          }
        >

          <Route path="/payments" element={<PaymentsList />} />
          <Route path="/payments/details/:id" element={<PaymentDetails />} />
          <Route path="/payments/card-details/:paymentId?" element={<PaymentCardDetails />} />
          <Route path="/payments/team/:teamId" element={<ParticipantPayment />} />

          <Route path="/fundraising" element={<FundraisingList />} />
          <Route path="/fundraising/create" element={<CreateFundraising />} />
          <Route path="/fundraising/:id" element={<FundraisingDetails />} />

          <Route
            path={DASHBOARD_STACK.ORGANISATION}
            element={<Organisation />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.TOURNAMENT_NAME}
            element={<TournamentName />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.TOURNAMENT_LOGO}
            element={<TournamentLogo />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_SPORT}
            element={<ChooseSport />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_TOURNAMENT}
            element={<ChooseTournament />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_DURATION_TYPE}
            element={<ChooseDurationType />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_VENUE_TYPE}
            element={<ChooseVenueType />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_RESULT_MANAGEMENT}
            element={<ChooseResultManagement />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.SET_MATCH_RULES}
            element={<SetMatchRules />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.SET_MATCH_SCHEDULE}
            element={<SetMatchSchedule />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CREATE_TOURNAMENT_DIVISION}
            element={<CreateTournamentDivision />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.TEAM_REGISTRATION_FEE}
            element={<TeamRegistrationFee />}
          />
          <Route
            path={
              DASHBOARD_STACK.CREATE_TOURNAMENT.CREATE_TOURNAMENT_TEAM_COUNT
            }
            element={<TeamCount />}
          />
          <Route
            path={DASHBOARD_STACK.CREATE_TOURNAMENT.CREATE_TOURNAMENT_GROUPS}
            element={<CreateTournamentGroups />}
          />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#041A30",
            border: "1px solid #192634",
            color: "#FEFCEB",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            width: "fit-content",
            maxWidth: "400px",
            borderRadius: "16px",
            padding: "14px 16px",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
          },
          success: {
            icon: <CheckCircle size={20} color="#4aed88" />,
          },
          error: {
            icon: <XCircle size={20} color="#FF7B7B" />,
            style: {
              borderColor: "rgba(255, 68, 68, 0.3)",
            },
          },
        }}
      />
    </>
  );
}

export default App;
